import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import { apiRequest } from "@/lib/api";
import { GRADE_OPTIONS, ROLE_OPTIONS, type Profile } from "../userMeta";

interface UserEditModalProps {
  open: boolean;
  onClose: () => void;
  profile: Profile | null;
  onSaved: () => void;
}

export default function UserEditModal({
  open,
  onClose,
  profile,
  onSaved,
}: UserEditModalProps) {
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("");
  const [grade, setGrade] = useState("");
  const [city, setCity] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [targetExam, setTargetExam] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name ?? "");
      setRole(profile.role ?? "");
      setGrade(profile.grade ?? "");
      setCity(profile.city ?? "");
      setBirthDate(profile.birth_date ?? "");
      setTargetExam(profile.target_exam ?? "");
      setError("");
    }
  }, [profile]);

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!profile) return;
    setSaving(true);
    setError("");

    try {
      await apiRequest(`/admin/users/${encodeURIComponent(profile.user_id ?? profile.id)}`, {
        method: "PUT",
        body: {
          role,
          full_name: fullName.trim(),
          grade_level: grade,
          city: city.trim(),
          birth_date: birthDate,
          target_exam: targetExam.trim(),
        },
      });

      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kayıt güncellenemedi.");
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Kullanıcıyı Düzenle"
      titleId="user-edit-title"
      id="user-edit-modal"
      maxWidthClass="max-w-md"
    >
      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label
            htmlFor="edit-full-name"
            className="block text-sm font-semibold text-foreground-800 mb-1.5"
          >
            Ad Soyad
          </label>
          <input
            id="edit-full-name"
            name="full_name"
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Ad Soyad"
            className="w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none transition-colors"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="edit-birth-date" className="block text-sm font-semibold text-foreground-800 mb-1.5">Doğum Tarihi</label>
            <input id="edit-birth-date" name="birth_date" type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} className="w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none transition-colors" />
          </div>
          <div>
            <label htmlFor="edit-target-exam" className="block text-sm font-semibold text-foreground-800 mb-1.5">Hedef Sınav</label>
            <input id="edit-target-exam" name="target_exam" type="text" value={targetExam} onChange={(e) => setTargetExam(e.target.value)} placeholder="Örn: YKS" className="w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none transition-colors" />
          </div>
        </div>

        <div>
          <label
            htmlFor="edit-role"
            className="block text-sm font-semibold text-foreground-800 mb-1.5"
          >
            Rol
          </label>
          <select
            id="edit-role"
            name="role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none transition-colors cursor-pointer"
          >
            <option value="">— Seçin —</option>
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="edit-grade"
            className="block text-sm font-semibold text-foreground-800 mb-1.5"
          >
            Sınıf / Seviye
          </label>
          <select
            id="edit-grade"
            name="grade"
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none transition-colors cursor-pointer"
          >
            <option value="">— Sınıf yok —</option>
            {GRADE_OPTIONS.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="edit-city"
            className="block text-sm font-semibold text-foreground-800 mb-1.5"
          >
            İl
          </label>
          <input
            id="edit-city"
            name="city"
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="İl"
            className="w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none transition-colors"
          />
        </div>

        {error && (
          <div className="rounded-md bg-accent-100/70 text-accent-800 px-3 py-2.5 text-sm">
            {error}
          </div>
        )}

        <div className="flex items-center gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-10 rounded-md border border-background-200 bg-background-50 text-foreground-700 text-sm font-semibold whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
          >
            Vazgeç
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 h-10 flex items-center justify-center gap-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                Kaydediliyor...
              </>
            ) : (
              "Kaydet"
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
